import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { lowerCase } from 'lodash-es';
import { Observable, catchError, map, of, retry, shareReplay } from 'rxjs';
import { Products } from '../../../app/interfaces/state-interface';
import {
    WebflowCard,
    WebflowCompany,
    WebflowPage,
    WebflowPageContent,
    WebflowRoot,
} from '../../interfaces/webflow-details.interface';
import { SentryService } from '../sentry/sentry.service';

type WebflowPageKind = 'signup' | 'login' | 'splash';
type WebflowTarget = 'daton' | 'pulse' | 'global';

/**
 * Page names exactly as authored in webflow-data.json. Signup and login pages use
 * underscores; splash pages use spaces. Both spellings are intentional.
 */
const WEBFLOW_PAGE_NAMES: Record<WebflowPageKind, Record<WebflowTarget, string>> = {
    signup: { daton: 'Daton_signup_page', pulse: 'Pulse_signup_page', global: 'Saras_signup_page' },
    login: { daton: 'Daton_login_page', pulse: 'Pulse_login_page', global: 'Saras_login_page' },
    splash: {
        daton: 'Daton Splash Screen Page',
        pulse: 'Pulse Splash Screen Page',
        global: 'Saras Splash Screen Page',
    },
};

/**
 * Fetches and shapes Webflow CMS content (signup, login, splash and product-listing pages)
 * for the signup and login screens.
 *
 * The raw payload is fetched from `/assets/webflow-data.json` on first use and cached for the
 * lifetime of the application via {@link webflowData$}.
 */
@Injectable({
    providedIn: 'root'
})
export class WebflowService {
    private readonly http = inject(HttpClient);
    private readonly sentry = inject(SentryService);

    private readonly webflowData$: Observable<WebflowRoot[]> = this.http
        .get<WebflowRoot[]>('/assets/webflow-data.json', {
            headers: new HttpHeaders({ skip: 'true' }),
        })
        .pipe(
            retry({ count: 2, delay: 1000 }),
            catchError((error) => {
                this.sentry.captureException(
                    error instanceof Error ? error : new Error(String(error)),
                    { context: 'Webflow data fetch', component: 'WebflowService' }
                );
                return of([] as WebflowRoot[]);
            }),
            shareReplay({ bufferSize: 1, refCount: false }),
        );

    private getProductPageByName(data: WebflowRoot[], pageName: string): WebflowPage | undefined {
        const productPages = data[0]?.fieldData['product-pages'] || [];
        return productPages.find((page) => page.fieldData.name === pageName);
    }

    /** Linked cards for a Webflow page, or `[]` if the page is absent or has none. */
    getLinkedCards(page: WebflowPage | undefined): WebflowCard[] {
        return page?.fieldData['linked-card'] || [];
    }

    /** Linked client companies for a Webflow page, or `[]` if the page is absent or has none. */
    getLinkedClients(page: WebflowPage | undefined): WebflowCompany[] {
        return page?.fieldData['linked-client'] || [];
    }

    private toPageContent(
        page: WebflowPage | undefined,
        options?: { includeLoginImage?: boolean }
    ): WebflowPageContent {
        const content: WebflowPageContent = {
            pageTitle: page?.fieldData['page-title'] || '',
            pageSubtitle: page?.fieldData['page-subtitle'] || '',
            pageSubTitle2: page?.fieldData['page-subtitle-2-optional'] || '',
            productName: page?.fieldData['product-name'] || '',
            headerImage: page?.fieldData['header-image']?.[0]?.url || '',
            description: page?.fieldData['description'] || '',
            linkedCards: this.getLinkedCards(page),
            linkedClients: this.getLinkedClients(page),
        };
        if (options?.includeLoginImage) {
            content.loginImage = page?.fieldData['login-image']?.[0]?.url || '';
        }
        return content;
    }

    private getPageContent$(
        kind: WebflowPageKind,
        product: Products,
        options?: { includeLoginImage?: boolean }
    ): Observable<WebflowPageContent> {
        return this.webflowData$.pipe(
            map(data => {
                const targetProduct = this.mapProductToTarget(product);
                const pageName = WEBFLOW_PAGE_NAMES[kind][targetProduct];
                const page = this.getProductPageByName(data, pageName);
                return this.toPageContent(page, options);
            })
        );
    }

    /**
     * Emits the Saras product-listing page as a raw Webflow page (not shaped content).
     *
     * @returns The raw product-listing page, or `undefined` if it is not present in the payload.
     */
    getSarasProductListingPage$(): Observable<WebflowPage | undefined> {
        return this.webflowData$.pipe(
            map(data => this.getProductPageByName(data, 'Saras_product_listing_page'))
        );
    }

    /**
     * Emits the signup page content for the given product.
     *
     * @param product Product whose signup page should be resolved.
     * @returns Shaped page content for the resolved signup page.
     */
    getSignupPageByProduct$(product: Products): Observable<WebflowPageContent> {
        return this.getPageContent$('signup', product);
    }

    /**
     * Emits the login page content for the given product, including `loginImage`.
     *
     * @param product Product whose login page should be resolved.
     * @returns Shaped page content for the resolved login page.
     */
    getLoginPageByProduct$(product: Products): Observable<WebflowPageContent> {
        return this.getPageContent$('login', product, { includeLoginImage: true });
    }

    /**
     * Emits the splash-screen page content for the given product.
     *
     * @param product Product whose splash screen should be resolved.
     * @returns Shaped page content for the resolved splash screen.
     */
    getSplashScreenPage$(product: Products): Observable<WebflowPageContent> {
        return this.getPageContent$('splash', product);
    }

    private mapProductToTarget(product: Products): WebflowTarget {
        switch (lowerCase(product)) {
            case 'daton':
                return 'daton';
            case 'pulse':
            case 'insights':
                return 'pulse';
            case 'global':
            case 'saras':
                return 'global';
            default:
                return 'global';
        }
    }
}
